/**
 * Job Runs Table Component
 * 
 * Displays execution history for all jobs.
 */
import React from 'react';
import { useBreakpoint } from '../../../../hooks/useBreakpoint';

const JobRunsTable = ({ runs, onRefresh }) => {
  //! Phone gets CARDS, not the table. Four columns are ~660-950px wide against a
  //! 343px screen, and the error message — the failure reason, available nowhere
  //! else on this page — was truncated at max-w-md (448px, wider than the whole
  //! viewport), so it was unreadable exactly when it mattered.
  const { isPhone } = useBreakpoint();

  // Format duration
  const formatDuration = (ms) => {
    if (!ms) return '-';
    // < 1 sec
    if (ms < 1000) return `${ms}ms`;
    // < 1 min
    if (ms < 60000) return `${(ms / 1000).toFixed(2)}s`;
    // > 1 min
    const mins = Math.floor(ms / 60000);
    const secs = ((ms % 60000) / 1000).toFixed(0);
    return `${mins}m ${secs}s`;
  };

  // Format time ago
  const formatTimeAgo = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffSecs = Math.floor(diffMs / 1000);
    
    if (diffSecs < 60) return 'Just now';
    if (diffSecs < 3600) return `${Math.floor(diffSecs / 60)}m ago`;
    if (diffSecs < 86400) return `${Math.floor(diffSecs / 3600)}h ago`;
    return `${Math.floor(diffSecs / 86400)}d ago`;
  };

  //--> Status -> ds-badge variant. Shared by the desktop table and the phone
  //    cards so the two presentations can't drift.
  const getStatusVariant = (status) => {
    switch (status) {
      case 'success': return 'profit';
      case 'failed': return 'loss';
      case 'running': return 'info animate-pulse';
      default: return 'neutral';
    }
  };

  //--> Shared empty state for both presentations.
  const emptyState = (
    <div className="flex flex-col items-center gap-3 py-12 text-center text-fg-3">
      <span className="text-4xl opacity-20">📜</span>
      <span className="text-sm">No execution history found</span>
    </div>
  );

  //--> PHONE: one card per run. Read-only, so no actions — the point is that the
  //    error message is fully readable instead of truncated off-screen.
  const renderRunCards = () => (
    <div className="flex flex-col gap-3">
      {runs.map((run) => (
        <div key={run.id} className="rounded-xl border border-[var(--divider)] bg-bg-2 p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="truncate font-medium text-fg-1">{run.job_name}</div>
              <div className="mt-0.5 flex items-center gap-2 font-mono text-xs text-fg-2">
                <span>#{run.id.slice(0, 8)}</span>
                {run.triggered_by === 'manual' && (
                  <span className="ds-badge xs role">
                    Manual
                  </span>
                )}
              </div>
            </div>
            <span className={`ds-badge sm shrink-0 ${getStatusVariant(run.status)}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${
                run.status === 'running' ? 'bg-current animate-ping' : 'bg-current'
              }`} />
              {run.status}
            </span>
          </div>

          <dl className="mt-3 grid grid-cols-2 gap-3 border-t border-[var(--divider)] pt-3 text-xs">
            <div className="min-w-0">
              <dt className="font-semibold text-fg-3">Completed</dt>
              <dd className="mt-1 text-fg-1">
                {run.completed_at ? (
                  <>
                    {new Date(run.completed_at).toLocaleString(undefined, {
                      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                    })}
                    <span className="mt-0.5 block text-fg-2">{formatTimeAgo(run.completed_at)}</span>
                  </>
                ) : (
                  <span className="font-medium text-accent animate-pulse">Running...</span>
                )}
              </dd>
            </div>
            <div className="min-w-0">
              <dt className="font-semibold text-fg-3">Duration</dt>
              <dd className="mt-1 font-mono text-fg-2">{formatDuration(run.duration_ms)}</dd>
            </div>
          </dl>

          {run.error_message && (
            /* Wraps, never truncates: this is the only place the failure reason
               appears anywhere on this screen. */
            <div className="mt-3 rounded border border-[var(--divider)] bg-[var(--badge-loss-bg)] px-2 py-1.5 font-mono text-xs break-words text-[var(--badge-loss-fg)]">
              {run.error_message}
            </div>
          )}
        </div>
      ))}
      {runs.length === 0 && (
        <div className="rounded-xl border border-[var(--divider)] bg-bg-2">{emptyState}</div>
      )}
    </div>
  );

  const renderRunTable = () => (
    <div className="bg-bg-2 border border-[var(--divider)] rounded-xl overflow-hidden shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="border-b border-[var(--divider)] bg-bg-1/50 text-fg-2 text-xs uppercase tracking-wider font-semibold">
              <th className="py-4 px-6">Status</th>
              <th className="py-4 px-6">Job Reference</th>
              <th className="py-4 px-6">Completed</th>
              <th className="py-4 px-6 text-right">Duration</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--divider)]">
            {runs.length === 0 ? (
              <tr>
                <td colSpan="4">{emptyState}</td>
              </tr>
            ) : (
              runs.map((run) => (
                <tr 
                  key={run.id} 
                  className="group hover:bg-bg-3/30 transition-colors"
                >
                  {/* Status Column */}
                  <td className="py-4 px-6 align-top">
                    <div className="flex flex-col gap-1.5">
                      <span className={`ds-badge sm w-fit ${getStatusVariant(run.status)}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${
                          run.status === 'running' ? 'bg-current animate-ping' : 'bg-current'
                        }`} />
                        {run.status}
                      </span>
                    </div>
                  </td>

                  {/* Job Reference Column */}
                  <td className="py-4 px-6 align-top">
                    <div className="flex flex-col gap-0.5">
                      <span className="font-medium text-fg-2 group-hover:text-accent transition-colors">
                        {run.job_name}
                      </span>
                      <div className="flex items-center gap-2 text-xs text-fg-2 font-mono">
                        <span>#{run.id.slice(0, 8)}</span>
                        {run.triggered_by === 'manual' && (
                          <span className="ds-badge xs role">
                            Manual
                          </span>
                        )}
                      </div>
                      {run.error_message && (
                        <div className="mt-1.5 text-xs text-[var(--badge-loss-fg)] font-mono bg-[var(--badge-loss-bg)] px-2 py-1 rounded border border-[var(--divider)] w-fit max-w-md truncate">
                          {run.error_message}
                        </div>
                      )}
                    </div>
                  </td>

                  {/* Completed Column */}
                  <td className="py-4 px-6 align-top">
                    <div className="flex flex-col">
                      <span className="text-fg-1 text-sm">
                        {run.completed_at 
                          ? new Date(run.completed_at).toLocaleString(undefined, { 
                              month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' 
                            })
                          : <span className="text-accent animate-pulse font-medium">Running...</span>
                        }
                      </span>
                      {run.completed_at && (
                        <span className="text-xs text-fg-2 mt-0.5">
                          {formatTimeAgo(run.completed_at)}
                        </span>
                      )}
                    </div>
                  </td>

                  {/* Duration Column */}
                  <td className="py-4 px-6 align-top text-right">
                     <span className={`font-mono text-sm ${
                       !run.duration_ms ? 'text-fg-2' : 'text-fg-3'
                     }`}>
                       {formatDuration(run.duration_ms)}
                     </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  //--> Cards on a phone, table on desktop.
  return isPhone ? renderRunCards() : renderRunTable();
};

export default JobRunsTable;
